import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "./config";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import "react-toastify/dist/ReactToastify.css";

const FinanceiroLayout2 = () => {
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("schools"); // saas | schools
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTheme = localStorage.getItem('theme');
      if (currentTheme !== theme) {
        setTheme(currentTheme);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [theme]);

  useEffect(() => {
    const type = parseInt(localStorage.getItem("userType"), 10);
    const currentUserName = String(localStorage.getItem("userName") || "");
    const isIvanGestor = type === 1 && currentUserName.toLowerCase().includes("ivan");
    setUserType(type);
    
    if (!isIvanGestor) {
        toast.error("Acesso não autorizado.");
        navigate("/home");
        return;
    }
    
    // Call setup-db to ensure tables exist (Admin only)
    if (type === 1) {
        setupDb();
    }
  }, [navigate]);

  const setupDb = async () => {
      try {
          const token = localStorage.getItem('token');
          await axios.get(`${API_BASE_URL}/financeiro2/setup-db`, {
              headers: { Authorization: `Bearer ${token}` }
          });
      } catch (e) {
          console.error("Erro ao configurar DB", e);
      } finally {
          setLoading(false);
      }
  };

  if (loading) return <div>Carregando...</div>;

  return (
    <div className={`card p-4 border-0 shadow-sm ${theme === 'dark' ? 'bg-dark text-white' : ''}`} style={{ borderRadius: '15px' }}>
        <div className="d-flex justify-content-between align-items-center mb-5 pb-3 border-bottom">
            <div className="d-flex align-items-center gap-3">
                <div className={`p-3 rounded-circle ${theme === 'dark' ? 'bg-dark border border-secondary' : 'bg-primary-subtle'}`}>
                    <Icon icon="solar:card-transfer-bold" className={`fs-2 ${theme === 'dark' ? 'text-white' : 'text-primary'}`} />
                </div>
                <div>
                    <h3 className="mb-0 fw-bold">Financeiro 2.0</h3>
                    <p className={`mb-0 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Gestão de Assinaturas e Integração de Pagamentos</p>
                </div>
            </div>
            
            <div className={`d-flex gap-2 p-1 rounded-pill border ${theme === 'dark' ? 'bg-dark border-secondary' : 'bg-light'}`}>
                <button 
                    className={`btn rounded-pill d-flex align-items-center gap-2 px-4 ${activeTab === 'schools' ? 'btn-primary' : (theme === 'dark' ? 'text-white-50' : 'text-secondary')}`}
                    onClick={() => setActiveTab('schools')}
                >
                    <Icon icon="solar:shop-bold" />
                    Escolas
                </button>
                <button 
                    className={`btn rounded-pill d-flex align-items-center gap-2 px-4 ${activeTab === 'saas' ? 'btn-primary' : (theme === 'dark' ? 'text-white-50' : 'text-secondary')}`}
                    onClick={() => setActiveTab('saas')}
                >
                    <Icon icon="solar:shield-star-bold" />
                    Franquia (SaaS)
                </button>
            </div>
        </div>

        {activeTab === 'saas' && <SaasView theme={theme} />}
        {activeTab === 'schools' && <SchoolsAdminView theme={theme} />}
        
        <ToastContainer />
    </div>
  );
};

const SaasView = ({ theme }) => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pixLoading, setPixLoading] = useState(false);
    const [pixData, setPixData] = useState(null);
    const [payingPix, setPayingPix] = useState(false);
    const [cardBrand, setCardBrand] = useState("");
    const [cardLast4, setCardLast4] = useState("");
    const [subscribing, setSubscribing] = useState(false);
    const [canceling, setCanceling] = useState(false);

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/financeiro2/saas-status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStatus(res.data);
        } catch (error) {
            console.error(error);
            toast.error("Erro ao carregar status SaaS");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div>Carregando status...</div>;

    const isActive = true; // status?.status === 'active'; (Bloqueio desativado conforme solicitado)
    const amount = status?.monthly_amount ? Number(status.monthly_amount).toFixed(2) : '570.00';
    const isCardSubscribed = status?.payment_method === 'card' && Number(status?.auto_renew) === 1;
    const pixCopyPaste = pixData?.copyPaste || status?.pix_copy_paste || "";

    const handleGeneratePix = async () => {
        setPixLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE_URL}/financeiro2/saas-generate-pix`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPixData(res.data);
            toast.success("Cobrança PIX gerada (simulação)");
        } catch (error) {
            toast.error(error.response?.data?.error || "Erro ao gerar PIX");
        } finally {
            setPixLoading(false);
        }
    };

    const handleConfirmPixPay = async () => {
        setPayingPix(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/financeiro2/saas-pay`, { paymentMethod: "pix", autoRenew: false }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Pagamento via PIX registrado (simulação)");
            fetchStatus();
        } catch (error) {
            toast.error(error.response?.data?.error || "Erro ao registrar pagamento");
        } finally {
            setPayingPix(false);
        }
    };

    const handleSubscribeCard = async () => {
        const last4 = String(cardLast4 || '').replace(/\D+/g, '').slice(-4);
        if (last4.length !== 4) {
            toast.warning("Informe os 4 últimos dígitos do cartão");
            return;
        }
        setSubscribing(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/financeiro2/saas-subscribe-card`, { cardLast4: last4, cardBrand }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Assinatura via cartão ativada (simulação)");
            setCardLast4("");
            setCardBrand("");
            fetchStatus();
        } catch (error) {
            toast.error(error.response?.data?.error || "Erro ao ativar assinatura");
        } finally {
            setSubscribing(false);
        }
    };

    const handleCancelCard = async () => {
        setCanceling(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/financeiro2/saas-cancel-card`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Assinatura via cartão cancelada");
            fetchStatus();
        } catch (error) {
            toast.error(error.response?.data?.error || "Erro ao cancelar assinatura");
        } finally {
            setCanceling(false);
        }
    };

    const handleCopyPix = async () => {
        if (!pixCopyPaste) return;
        try {
            await navigator.clipboard.writeText(pixCopyPaste);
            toast.success("PIX copia e cola copiado");
        } catch (error) {
            toast.error("Não foi possível copiar");
        }
    };

    return (
        <div className="row justify-content-center">
            <div className="col-lg-8">
                <div className={`card overflow-hidden border-0 shadow-sm ${theme === 'dark' ? 'bg-dark border border-secondary' : 'bg-white'}`}>
                    <div className={`card-header p-4 ${isActive ? 'bg-success-subtle' : 'bg-danger-subtle'}`}>
                        <div className="d-flex align-items-center gap-3">
                            <div className={`p-2 rounded-circle ${isActive ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                                <Icon icon={isActive ? "solar:check-circle-bold" : "solar:forbidden-circle-bold"} className="fs-3" />
                            </div>
                            <div>
                                <h5 className={`mb-0 fw-bold ${isActive ? 'text-success' : 'text-danger'}`}>
                                    {isActive ? 'Assinatura Ativa' : 'Assinatura Bloqueada'}
                                </h5>
                                <span className="small opacity-75">
                                    {isActive ? 'Todos os serviços estão operando normalmente.' : 'O acesso foi suspenso devido a pendências financeiras.'}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="card-body p-4">
                        <div className="row g-4 mb-4">
                            <div className="col-md-6">
                                <div className={`p-3 rounded-3 h-100 border ${theme === 'dark' ? 'border-secondary bg-transparent' : 'bg-light'}`}>
                                    <label className={`d-block small mb-1 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Plano Atual</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon="solar:crown-star-bold" className="text-warning fs-4" />
                                        <span className="fw-bold fs-5">{status?.plan_name || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className={`p-3 rounded-3 h-100 border ${theme === 'dark' ? 'border-secondary bg-transparent' : 'bg-light'}`}>
                                    <label className={`d-block small mb-1 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Próximo Vencimento</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon="solar:calendar-date-bold" className="text-primary fs-4" />
                                        <span className="fw-bold fs-5">
                                            {status?.next_payment_date ? new Date(status.next_payment_date).toLocaleDateString() : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className={`p-3 rounded-3 h-100 border ${theme === 'dark' ? 'border-secondary bg-transparent' : 'bg-light'}`}>
                                    <label className={`d-block small mb-1 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Mensalidade</label>
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="solar:wallet-money-bold" className="text-success fs-4" />
                                            <span className="fw-bold fs-5">R$ {amount}</span>
                                        </div>
                                        <span className={`small ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>
                                            {isCardSubscribed ? `Assinatura via cartão ativa (${status?.card_brand || 'Cartão'} •••• ${status?.card_last4 || '----'})` : 'Pagamento avulso via PIX ou assinatura via cartão'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!isActive && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 mb-4">
                                <Icon icon="solar:danger-triangle-bold" className="fs-4" />
                                <div>
                                    <strong>Atenção:</strong> O bloqueio afeta todas as escolas vinculadas a esta franquia. Regularize imediatamente.
                                </div>
                            </div>
                        )}

                        <div className="row g-3 pt-3 border-top">
                            <div className="col-md-6">
                                <div className={`p-3 rounded-3 border h-100 ${theme === 'dark' ? 'border-secondary bg-transparent' : 'bg-light'}`}>
                                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="solar:qr-code-bold" className="text-primary fs-4" />
                                            <div className="fw-bold">Pagar com PIX</div>
                                        </div>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={handleGeneratePix}
                                            disabled={pixLoading}
                                        >
                                            {pixLoading ? "Gerando..." : "Gerar PIX"}
                                        </button>
                                    </div>

                                    {pixCopyPaste ? (
                                        <>
                                            <label className={`form-label small ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Copia e cola</label>
                                            <textarea
                                                className={`form-control mb-2 ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}
                                                rows={3}
                                                value={pixCopyPaste}
                                                readOnly
                                            />
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-sm btn-primary w-100" onClick={handleCopyPix}>
                                                    Copiar
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-success w-100"
                                                    onClick={handleConfirmPixPay}
                                                    disabled={payingPix}
                                                >
                                                    {payingPix ? "Confirmando..." : "Confirmar pagamento"}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className={`small ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>
                                            Gere um PIX e depois confirme o pagamento para renovar a mensalidade.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className={`p-3 rounded-3 border h-100 ${theme === 'dark' ? 'border-secondary bg-transparent' : 'bg-light'}`}>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <Icon icon="solar:card-bold" className="text-success fs-4" />
                                        <div className="fw-bold">Assinar com Cartão</div>
                                    </div>

                                    {isCardSubscribed ? (
                                        <>
                                            <div className={`small mb-3 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>
                                                Débito mensal automático (simulação). Ao vencer, a renovação é aplicada automaticamente quando esta tela for carregada.
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-danger w-100"
                                                onClick={handleCancelCard}
                                                disabled={canceling}
                                            >
                                                {canceling ? "Cancelando..." : "Cancelar assinatura"}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="row g-2">
                                                <div className="col-12">
                                                    <label className={`form-label small ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Bandeira (opcional)</label>
                                                    <select
                                                        className={`form-select ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}
                                                        value={cardBrand}
                                                        onChange={(e) => setCardBrand(e.target.value)}
                                                    >
                                                        <option value="">Selecione</option>
                                                        <option value="Visa">Visa</option>
                                                        <option value="Mastercard">Mastercard</option>
                                                        <option value="Elo">Elo</option>
                                                        <option value="Amex">Amex</option>
                                                    </select>
                                                </div>
                                                <div className="col-12">
                                                    <label className={`form-label small ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Últimos 4 dígitos</label>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        maxLength={4}
                                                        className={`form-control ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}
                                                        value={cardLast4}
                                                        onChange={(e) => setCardLast4(e.target.value)}
                                                        placeholder="0000"
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                className="btn btn-sm btn-success w-100 mt-3"
                                                onClick={handleSubscribeCard}
                                                disabled={subscribing}
                                            >
                                                {subscribing ? "Ativando..." : "Ativar assinatura"}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SchoolsAdminView = ({ theme }) => {
    const [schools, setSchools] = useState([]);
    
    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API_BASE_URL}/financeiro2/schools`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSchools(res.data);
            } catch (error) {
                toast.error("Erro ao carregar escolas");
            }
        };
        fetchSchools();
    }, []);

    const connectedCount = schools.filter(s => s.cp_ec_mp_status === 'connected').length;
    const disconnectedCount = schools.length - connectedCount;

    return (
        <div>
            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className={`p-3 rounded-3 border d-flex align-items-center gap-3 ${theme === 'dark' ? 'bg-dark border-secondary' : 'bg-white'}`}>
                        <div className="p-3 rounded-circle bg-primary-subtle text-primary">
                            <Icon icon="solar:shop-bold" className="fs-3" />
                        </div>
                        <div>
                            <h6 className={`mb-0 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Total Escolas</h6>
                            <h4 className="mb-0 fw-bold">{schools.length}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className={`p-3 rounded-3 border d-flex align-items-center gap-3 ${theme === 'dark' ? 'bg-dark border-secondary' : 'bg-white'}`}>
                        <div className="p-3 rounded-circle bg-success-subtle text-success">
                            <Icon icon="solar:link-circle-bold" className="fs-3" />
                        </div>
                        <div>
                            <h6 className={`mb-0 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Conectadas (MP)</h6>
                            <h4 className="mb-0 fw-bold text-success">{connectedCount}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className={`p-3 rounded-3 border d-flex align-items-center gap-3 ${theme === 'dark' ? 'bg-dark border-secondary' : 'bg-white'}`}>
                        <div className="p-3 rounded-circle bg-warning-subtle text-warning">
                            <Icon icon="solar:link-broken-bold" className="fs-3" />
                        </div>
                        <div>
                            <h6 className={`mb-0 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Pendentes</h6>
                            <h4 className="mb-0 fw-bold text-warning">{disconnectedCount}</h4>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`card border-0 shadow-sm mb-4 ${theme === 'dark' ? 'bg-dark border border-secondary' : 'bg-white'}`}>
                <div className="card-header bg-transparent border-bottom py-3">
                    <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                        <Icon icon="solar:list-check-bold" className="text-primary" />
                        Lista de Escolas
                    </h5>
                </div>
                <div className="table-responsive">
                    <table className={`table mb-0 align-middle ${theme === 'dark' ? 'table-dark' : 'table-hover'}`}>
                        <thead className={theme === 'dark' ? '' : 'bg-light'}>
                            <tr>
                                <th className="py-3 ps-4">Escola</th>
                                <th>Status Integração</th>
                                <th>Conta MP (ID)</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schools.map(school => (
                                <tr key={school.cp_ec_id}>
                                    <td className="ps-4">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center fw-bold" style={{width: 40, height: 40}}>
                                                {school.cp_ec_nome.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="fw-semibold">{school.cp_ec_nome}</div>
                                                <small className={`small ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>ID: {school.cp_ec_id}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge rounded-pill px-3 py-2 ${school.cp_ec_mp_status === 'connected' ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                                            <span className={`d-inline-block rounded-circle me-2 ${school.cp_ec_mp_status === 'connected' ? 'bg-success' : 'bg-secondary'}`} style={{width: 8, height: 8}}></span>
                                            {school.cp_ec_mp_status === 'connected' ? 'Conectado' : 'Desconectado'}
                                        </span>
                                    </td>
                                    <td>
                                        {school.cp_ec_mp_user_id ? (
                                            <code className="text-primary">{school.cp_ec_mp_user_id}</code>
                                        ) : (
                                            <span className="text-secondary opacity-50">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <button className="btn btn-sm btn-icon btn-link text-secondary">
                                            <Icon icon="solar:menu-dots-bold" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            
            <div className="row g-4">
                <div className="col-md-6">
                    <div className={`card h-100 border-0 shadow-sm ${theme === 'dark' ? 'bg-dark border border-secondary' : 'bg-white'}`}>
                        <div className="card-header bg-transparent border-bottom py-3">
                            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                <Icon icon="solar:test-tube-bold" className="text-info" />
                                Simulador de Conexão
                            </h6>
                        </div>
                        <div className="card-body">
                            <p className={`small mb-3 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Simule como a escola vê o processo de conexão OAuth.</p>
                            <SchoolConnectionSimulator theme={theme} />
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className={`card h-100 border-0 shadow-sm ${theme === 'dark' ? 'bg-dark border border-secondary' : 'bg-white'}`}>
                        <div className="card-header bg-transparent border-bottom py-3">
                            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                <Icon icon="solar:wallet-money-bold" className="text-success" />
                                Simulador de Cobrança
                            </h6>
                        </div>
                        <div className="card-body">
                            <p className={`small mb-3 ${theme === 'dark' ? 'text-white-50' : 'text-secondary'}`}>Crie cobranças de teste usando as credenciais da escola.</p>
                            <BillingSimulator theme={theme} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const BillingSimulator = ({ theme }) => {
    const [schoolId, setSchoolId] = useState("");
    const [amount, setAmount] = useState("100.00");

    const handleCreateCharge = async () => {
        if (!schoolId) return toast.warning("Informe o ID da escola");
        
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE_URL}/financeiro2/simulate-charge`, {
                schoolId,
                amount,
                description: "Mensalidade Teste"
            }, {
                 headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(res.data.message);
            console.log("Charge Details:", res.data.details);
        } catch (error) {
            toast.error(error.response?.data?.error || "Erro ao criar cobrança");
        }
    };

    return (
        <div>
            <div className="mb-3">
                <label className="form-label small">ID da Escola</label>
                <div className="input-group">
                    <span className={`input-group-text ${theme === 'dark' ? 'bg-dark border-secondary text-white-50' : 'bg-light'}`}>
                        <Icon icon="solar:hashtag-bold" />
                    </span>
                    <input 
                        type="number" 
                        className={`form-control ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}
                        placeholder="Ex: 1" 
                        value={schoolId}
                        onChange={e => setSchoolId(e.target.value)}
                    />
                </div>
            </div>
            <div className="mb-4">
                <label className="form-label small">Valor (R$)</label>
                <div className="input-group">
                    <span className={`input-group-text ${theme === 'dark' ? 'bg-dark border-secondary text-white-50' : 'bg-light'}`}>
                        R$
                    </span>
                    <input 
                        type="number" 
                        className={`form-control ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}
                        placeholder="0,00" 
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                    />
                </div>
            </div>
            <button className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2" onClick={handleCreateCharge}>
                <Icon icon="solar:card-send-bold" />
                Processar Cobrança Teste
            </button>
        </div>
    );
};

const SchoolConnectionSimulator = ({ theme }) => {
    const [schoolId, setSchoolId] = useState("");
    const [status, setStatus] = useState(null);

    const checkStatus = async () => {
        if (!schoolId) return;
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/financeiro2/my-school-mp?schoolId=${schoolId}`, {
                 headers: { Authorization: `Bearer ${token}` }
            });
            setStatus(res.data);
        } catch (error) {
            toast.error("Erro ao verificar status da escola");
            setStatus(null);
        }
    };

    const handleConnect = async () => {
        try {
            const token = localStorage.getItem('token');
            // Simulate OAuth callback
            await axios.post(`${API_BASE_URL}/financeiro2/connect-mp`, {
                schoolId,
                mpUserId: "MP-" + Math.floor(Math.random() * 1000000),
                mpAccessToken: "TEST-TOKEN-" + Date.now()
            }, {
                 headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Conectado com sucesso!");
            checkStatus();
        } catch (error) {
            toast.error("Erro ao conectar");
        }
    };

    const handleDisconnect = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/financeiro2/disconnect-mp`, { schoolId }, {
                 headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Desconectado com sucesso!");
            checkStatus();
        } catch (error) {
            toast.error("Erro ao desconectar");
        }
    };

    return (
        <div>
            <label className="form-label small">ID da Escola</label>
            <div className="input-group mb-4">
                 <input 
                    type="text" 
                    className={`form-control ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}
                    placeholder="ID da Escola" 
                    value={schoolId}
                    onChange={e => setSchoolId(e.target.value)}
                />
                <button className={`btn ${theme === 'dark' ? 'btn-light' : 'btn-dark'}`} onClick={checkStatus}>
                    <Icon icon="solar:magnifer-bold" />
                </button>
            </div>
            
            {status && (
                <div className={`p-3 rounded-3 border mb-3 ${status.cp_ec_mp_status === 'connected' ? 'bg-success-subtle border-success-subtle' : 'bg-warning-subtle border-warning-subtle'}`}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <div className={`fw-bold ${status.cp_ec_mp_status === 'connected' ? 'text-success' : 'text-warning'}`}>
                                {status.cp_ec_mp_status === 'connected' ? 'Conectado' : 'Não Conectado'}
                            </div>
                            {status.cp_ec_mp_user_id && <small className="text-secondary">MP ID: {status.cp_ec_mp_user_id}</small>}
                        </div>
                        <Icon 
                            icon={status.cp_ec_mp_status === 'connected' ? "solar:check-circle-bold" : "solar:close-circle-bold"} 
                            className={`fs-4 ${status.cp_ec_mp_status === 'connected' ? 'text-success' : 'text-warning'}`} 
                        />
                    </div>
                    
                    <div className="mt-3">
                        {status.cp_ec_mp_status !== 'connected' ? (
                            <button className="btn btn-sm btn-primary w-100 d-flex align-items-center justify-content-center gap-2" onClick={handleConnect}>
                                <Icon icon="logos:mercadopago-icon" />
                                Simular Conexão OAuth
                            </button>
                        ) : (
                            <button className="btn btn-sm btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2" onClick={handleDisconnect}>
                                <Icon icon="solar:link-broken-bold" />
                                Desconectar Integração
                            </button>
                        )}
                    </div>
                </div>
            )}
            
            {!status && schoolId && (
                <div className="text-center text-secondary py-3">
                    <small>Clique na lupa para buscar o status atual.</small>
                </div>
            )}
        </div>
    );
};

export default FinanceiroLayout2;
